import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import Modal from '@orn/shared-web/components/Modal/Modal';
import { TrashIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import formatTimeAgo from '@orn/shared-web/utils/formatTimeAgo';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { AssistantMemoryScope } from '../../__generated__/globalTypes';
import AdminSelectUser, { type IAdminSelectUserSelection } from '../../components/admin/AdminSelectUser';
import Button from '../../components/Button';
import IconButton from '../../components/IconButton';
import Input from '../../components/Input';
import Table from '../../components/Table';
import { useToast } from '../../components/Toast';
import { assistantSessionDeepLinkPath } from '../../paths';
import { useAuthState } from '../../providers/AuthProvider';

import { prefixReason } from './prefixReason';

import type {
  AdminDeleteAllAssistantMemoryForUserMutation,
  AdminDeleteAllAssistantMemoryForUserMutationVariables,
  AdminDeleteAssistantMemoryMutation,
  AdminDeleteAssistantMemoryMutationVariables,
  AdminGetAssistantMemoryForUserQuery,
  AdminGetAssistantMemoryForUserQueryVariables,
  AdminListUsersForAssistantMemoryQuery,
} from './__generated__/AssistantMemoryScene.gql';

const ADMIN_GET_ASSISTANT_MEMORY = gql`
  query AdminGetAssistantMemoryForUser($userId: ID!) {
    adminGetAssistantMemoryForUser(userId: $userId) {
      hasMore
      memories {
        id
        scope
        body
        tags
        importance
        effectiveImportance
        source
        sourceThreadId
        userId
        familyId
        createdAt
        updatedAt
      }
    }
  }
`;

const ADMIN_DELETE_ASSISTANT_MEMORY = gql`
  mutation AdminDeleteAssistantMemory($id: ID!, $scope: AssistantMemoryScope!, $reason: String!) {
    adminDeleteAssistantMemory(id: $id, scope: $scope, reason: $reason)
  }
`;

const ADMIN_DELETE_ALL_ASSISTANT_MEMORY = gql`
  mutation AdminDeleteAllAssistantMemoryForUser($userId: ID!, $reason: String!) {
    adminDeleteAllAssistantMemoryForUser(userId: $userId, reason: $reason)
  }
`;

// Same shape as the picker — re-issued via useQuery so the email-by-id
// lookup is available even before the picker mounts.
const ADMIN_LIST_USERS_FOR_ASSISTANT_MEMORY = gql`
  query AdminListUsersForAssistantMemory {
    adminListUsers {
      id
      email
    }
  }
`;

type MemoryRow = AdminGetAssistantMemoryForUserQuery['adminGetAssistantMemoryForUser']['memories'][number];

type SortKey =
  | 'scope'
  | 'importance'
  | 'effectiveImportance'
  | 'body'
  | 'tags'
  | 'source'
  | 'sourceThread'
  | 'user'
  | 'updatedAt'
  | 'createdAt';
type SortDirection = 'asc' | 'desc';

type ColumnDef = { key: SortKey; label: string; align?: 'left' | 'right' };

const COLUMNS: ColumnDef[] = [
  { key: 'scope', label: 'Scope' },
  { key: 'importance', label: 'Imp', align: 'right' },
  { key: 'effectiveImportance', label: 'Eff imp', align: 'right' },
  { key: 'body', label: 'Body' },
  { key: 'tags', label: 'Tags' },
  { key: 'source', label: 'Source' },
  { key: 'sourceThread', label: 'Thread' },
  { key: 'user', label: 'User' },
  { key: 'updatedAt', label: 'Updated' },
  { key: 'createdAt', label: 'Created' },
];

const SCOPE_BADGE: Record<AssistantMemoryScope, string> = {
  [AssistantMemoryScope.User]: 'bg-bg-secondary text-fg-primary border-border-primary',
  [AssistantMemoryScope.Family]: 'bg-bg-secondary text-fg-brand-primary border-border-brand',
};

/**
 * Memory sub-tab of the Assistant tool. Operator picks a user; the pane
 * loads every non-invalidated memory visible to that user — their
 * private user-scope rows plus their family's shared family-scope rows —
 * and renders them in one sortable table. Per-row delete confirms via
 * modal; the "Delete all" action wipes every row (active + invalidated)
 * across both scopes in one shot with a required reason. Deletes are
 * hard — the row is dropped from the DB rather than soft-flagged — so
 * the operator can credibly honor a "permanently forget this" request.
 */
const AssistantMemoryScene = () => {
  const [selection, setSelection] = React.useState<IAdminSelectUserSelection | null>(null);
  const [sortKey, setSortKey] = React.useState<SortKey>('effectiveImportance');
  const [sortDirection, setSortDirection] = React.useState<SortDirection>('desc');
  const [rowDelete, setRowDelete] = React.useState<MemoryRow | null>(null);
  const [rowReason, setRowReason] = React.useState('');
  const [bulkDeleteOpen, setBulkDeleteOpen] = React.useState(false);
  const [bulkReason, setBulkReason] = React.useState('');
  const [actionLoading, setActionLoading] = React.useState(false);
  const [expandedBodies, setExpandedBodies] = React.useState<Set<string>>(new Set());

  const { showToast } = useToast();
  const { user: mopsUser } = useAuthState();

  const userId = selection?.userId ?? null;

  const { data: usersData } = useQuery<AdminListUsersForAssistantMemoryQuery>(ADMIN_LIST_USERS_FOR_ASSISTANT_MEMORY);
  const userEmailById = React.useMemo(() => {
    const map = new Map<string, string>();
    usersData?.adminListUsers?.forEach((u) => map.set(u.id, u.email));
    return map;
  }, [usersData]);

  const [loadMemory, { data: memoryData, loading: memoryLoading, refetch: refetchMemory }] = useLazyQuery<
    AdminGetAssistantMemoryForUserQuery,
    AdminGetAssistantMemoryForUserQueryVariables
  >(ADMIN_GET_ASSISTANT_MEMORY);

  const [deleteRow] = useMutation<AdminDeleteAssistantMemoryMutation, AdminDeleteAssistantMemoryMutationVariables>(
    ADMIN_DELETE_ASSISTANT_MEMORY
  );

  const [deleteAll] = useMutation<
    AdminDeleteAllAssistantMemoryForUserMutation,
    AdminDeleteAllAssistantMemoryForUserMutationVariables
  >(ADMIN_DELETE_ALL_ASSISTANT_MEMORY);

  React.useEffect(() => {
    if (userId) {
      void loadMemory({ variables: { userId } });
    }
  }, [userId, loadMemory]);

  const handleUserSelect = (next: IAdminSelectUserSelection | null) => {
    setSelection(next);
    setRowDelete(null);
    setRowReason('');
    setBulkDeleteOpen(false);
    setBulkReason('');
    setExpandedBodies(new Set());
  };

  const toggleBodyExpanded = (id: string) => {
    setExpandedBodies((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    // Numeric columns default to descending (highest importance first);
    // text/date columns default to ascending on first click.
    setSortDirection(key === 'importance' || key === 'effectiveImportance' ? 'desc' : 'asc');
  };

  // Memoize so the empty-array default doesn't trigger sortedMemories
  // on every render — the eslint exhaustive-deps rule otherwise warns.
  const memories = React.useMemo(() => memoryData?.adminGetAssistantMemoryForUser?.memories ?? [], [memoryData]);
  const hasMore = memoryData?.adminGetAssistantMemoryForUser?.hasMore ?? false;
  const selectedUserEmail = selection ? (userEmailById.get(selection.userId) ?? null) : null;

  const sortedMemories = React.useMemo(() => {
    const out = [...memories];
    out.sort((a, b) => compareRows(a, b, sortKey, userEmailById));
    if (sortDirection === 'desc') {
      out.reverse();
    }
    return out;
  }, [memories, sortKey, sortDirection, userEmailById]);

  const handleRowDeleteConfirm = async () => {
    if (!rowDelete || rowReason.trim().length === 0) {
      return;
    }
    setActionLoading(true);
    try {
      await deleteRow({
        variables: {
          id: rowDelete.id,
          scope: rowDelete.scope,
          reason: prefixReason(rowReason, mopsUser?.email ?? null),
        },
      });
      showToast({ type: 'success', message: 'Memory deleted.' });
      setRowDelete(null);
      setRowReason('');
      await refetchMemory();
    } catch {
      // ApolloProvider's errorLink reports GraphQL + network errors to Sentry.
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkDeleteConfirm = async () => {
    if (!userId || bulkReason.trim().length === 0) {
      return;
    }
    setActionLoading(true);
    try {
      const result = await deleteAll({
        variables: { userId, reason: prefixReason(bulkReason, mopsUser?.email ?? null) },
      });
      const count = result.data?.adminDeleteAllAssistantMemoryForUser ?? 0;
      showToast({
        type: 'success',
        message: count === 0 ? 'No memories to delete.' : `Deleted ${count} memories.`,
      });
      setBulkDeleteOpen(false);
      setBulkReason('');
      await refetchMemory();
    } catch {
      // ApolloProvider's errorLink reports GraphQL + network errors to Sentry.
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <>
      <div className="w-full max-w-md">
        <AdminSelectUser selectedUserId={selection?.userId ?? null} onSelectionChange={handleUserSelect} />
      </div>

      {!userId ? (
        <div className="text-fg-tertiary text-xs italic">Select a user to view assistant memories.</div>
      ) : memoryLoading ? (
        <div className="text-fg-secondary text-xs">Loading memories…</div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="text-fg-secondary text-xs">
              {selectedUserEmail ? (
                <>
                  Memories for family of <span className="font-mono">{selectedUserEmail}</span>
                </>
              ) : (
                'Memories'
              )}
              {memories.length > 0 && <span className="text-fg-tertiary"> · {memories.length} active</span>}
            </div>
            <Button
              color="danger"
              size="sm"
              isDisabled={memories.length === 0 || actionLoading}
              onClick={() => setBulkDeleteOpen(true)}
            >
              Delete all
            </Button>
          </div>

          {hasMore && (
            <div className="border-fg-warning-primary text-fg-warning-primary bg-bg-secondary border-l-2 px-3 py-2 text-xs">
              Showing 500 most recent memories per scope. Older rows hidden.
            </div>
          )}

          {memories.length === 0 ? (
            <div className="text-fg-tertiary text-xs italic">No memories recorded for this family.</div>
          ) : (
            <Table>
              <Table.Header sticky>
                <Table.Row>
                  {COLUMNS.map((col) => (
                    <Table.HeaderCell
                      key={col.key}
                      onClick={() => toggleSort(col.key)}
                      className={cx(
                        'hover:text-fg-primary cursor-pointer select-none',
                        col.align === 'right' && 'text-right'
                      )}
                    >
                      <span className="inline-flex items-baseline gap-1">
                        {col.label}
                        {sortKey === col.key && (
                          <span className="text-fg-quaternary text-[10px]">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                        )}
                      </span>
                    </Table.HeaderCell>
                  ))}
                  <Table.HeaderCell />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {sortedMemories.map((row) => {
                  const userEmail = userEmailById.get(row.userId);
                  return (
                    <Table.Row key={row.id}>
                      <Table.Cell>
                        <span
                          className={cx(
                            'inline-flex items-center border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider',
                            SCOPE_BADGE[row.scope]
                          )}
                        >
                          {row.scope}
                        </span>
                      </Table.Cell>
                      <Table.Cell className="text-right">{row.importance}</Table.Cell>
                      <Table.Cell className="text-right">{row.effectiveImportance.toFixed(2)}</Table.Cell>
                      <Table.Cell className="max-w-[480px]">
                        <button
                          type="button"
                          onClick={() => toggleBodyExpanded(row.id)}
                          className="hover:text-fg-primary block w-full cursor-pointer text-left font-mono text-[11px] leading-snug"
                          title={expandedBodies.has(row.id) ? 'Collapse' : 'Expand'}
                        >
                          {expandedBodies.has(row.id) ? (
                            <pre className="bg-bg-secondary text-fg-primary max-h-64 overflow-auto whitespace-pre-wrap break-words p-2">
                              <BodyContent body={row.body} />
                            </pre>
                          ) : (
                            <span className="line-clamp-2 break-words">
                              <BodyContent body={row.body} />
                            </span>
                          )}
                        </button>
                      </Table.Cell>
                      <Table.Cell>
                        <TagList tags={row.tags} />
                      </Table.Cell>
                      <Table.Cell mono muted className="text-[11px]">
                        {row.source}
                      </Table.Cell>
                      <Table.Cell mono muted className="text-[11px]">
                        {row.sourceThreadId ? (
                          <Link
                            to={assistantSessionDeepLinkPath({
                              userId: row.userId,
                              familyId: row.familyId,
                              threadId: row.sourceThreadId,
                            })}
                            className="text-fg-brand-primary hover:text-fg-brand-secondary underline-offset-2 hover:underline"
                            title={row.sourceThreadId}
                          >
                            {row.sourceThreadId.slice(0, 8)}
                          </Link>
                        ) : (
                          <span className="text-fg-quaternary">—</span>
                        )}
                      </Table.Cell>
                      <Table.Cell mono muted title={row.userId} className="text-[11px]">
                        {userEmail ?? row.userId}
                      </Table.Cell>
                      <Table.Cell mono muted className="text-[11px]">
                        {formatTimeAgo(row.updatedAt)}
                      </Table.Cell>
                      <Table.Cell
                        mono
                        muted
                        className="text-[11px]"
                        title={`${new Date(row.createdAt).toISOString()} (UTC)`}
                      >
                        {formatCreatedPacific(row.createdAt)}
                      </Table.Cell>
                      <Table.Cell>
                        <IconButton
                          label="Delete memory"
                          tone="danger"
                          disabled={actionLoading}
                          onClick={() => {
                            setRowDelete(row);
                            setRowReason('');
                          }}
                        >
                          <TrashIcon size={14} aria-hidden />
                        </IconButton>
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
          )}
        </div>
      )}

      <Modal isOpen={rowDelete !== null} onOpenChange={(open) => !open && setRowDelete(null)} size="md">
        <Modal.Header onClose={() => setRowDelete(null)}>Delete memory</Modal.Header>
        <Modal.Body>
          <div className="flex flex-col gap-4">
            <div className="text-fg-primary text-sm">
              This <span className="font-semibold">hard-deletes</span> the memory — the row is dropped from the DB and
              cannot be recovered. A reason is required and logged to CloudWatch.
            </div>
            {rowDelete && (
              <div className="text-fg-secondary border-border-primary border px-3 py-2 text-xs">
                <div className="font-mono text-[11px]">{rowDelete.body}</div>
              </div>
            )}
            <Input
              label="Reason"
              placeholder="e.g. user requested permanent removal, duplicate, model hallucination"
              value={rowReason}
              onChange={setRowReason}
            />
            <div className="flex justify-end gap-2">
              <Button color="secondary" size="sm" onClick={() => setRowDelete(null)}>
                Cancel
              </Button>
              <Button
                color="danger"
                size="sm"
                isDisabled={rowReason.trim().length === 0 || actionLoading}
                isLoading={actionLoading}
                onClick={() => void handleRowDeleteConfirm()}
              >
                Delete
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <Modal isOpen={bulkDeleteOpen} onOpenChange={(open) => !open && setBulkDeleteOpen(false)} size="md">
        <Modal.Header onClose={() => setBulkDeleteOpen(false)}>Delete all memories</Modal.Header>
        <Modal.Body>
          <div className="flex flex-col gap-4">
            <div className="text-fg-primary text-sm">
              This <span className="font-semibold">hard-deletes every</span> memory for this family across both user and
              family scopes (including already-invalidated rows). Rows cannot be recovered. A reason is required and
              logged to CloudWatch.
            </div>
            <Input
              label="Reason"
              placeholder="e.g. user requested permanent removal, ops cleanup"
              value={bulkReason}
              onChange={setBulkReason}
            />
            <div className="flex justify-end gap-2">
              <Button color="secondary" size="sm" onClick={() => setBulkDeleteOpen(false)}>
                Cancel
              </Button>
              <Button
                color="danger"
                size="sm"
                isDisabled={bulkReason.trim().length === 0 || actionLoading}
                isLoading={actionLoading}
                onClick={() => void handleBulkDeleteConfirm()}
              >
                Delete all
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

// Bracket-tag prefix convention from the assistant memory
// aggregates (`[todo]`, `[done]`, etc.). Match the *first* bracketed
// token only — `[meta][todo]` highlights `[meta]` and leaves the rest
// of the body untouched. Non-greedy `[^\]]*` so a body like
// `[todo] foo [done] bar` doesn't swallow the line.
const TAG_PREFIX_REGEX = /^\[[^\]]*\]/;

interface IBodyContentProps {
  body: string;
}

const BodyContent = ({ body }: IBodyContentProps) => {
  const match = body.match(TAG_PREFIX_REGEX);
  if (!match) {
    return <>{body}</>;
  }
  return (
    <>
      <span className="text-fg-success-primary font-bold">{match[0]}</span>
      {body.slice(match[0].length)}
    </>
  );
};

// Operators are SF-based; pin Created to Pacific so the column reads in
// one zone regardless of the operator's browser. `sv-SE` yields the dense
// `YYYY-MM-DD HH:MM:SS` shape the column already used.
const PACIFIC_CREATED_FORMATTER = new Intl.DateTimeFormat('sv-SE', {
  timeZone: 'America/Los_Angeles',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

function formatCreatedPacific(createdAt: string): string {
  return PACIFIC_CREATED_FORMATTER.format(new Date(createdAt));
}

// Reserved classifier tags (`sys:todo`, `sys:pref`, …) are system-managed,
// not free-form topic tags — render them distinctly so operators can tell
// the two apart at a glance.
const SYS_TAG_PREFIX = 'sys:';

// `min-w-0 truncate` lets each chip shrink and ellipsize when the row
// overflows the fixed-width cell, rather than the last chip getting
// hard-clipped by the container's `overflow-hidden`.
const TAG_CHIP_BASE = 'min-w-0 truncate border px-1.5 py-0.5 text-[10px] font-medium tracking-wide';
const SYS_TAG_CHIP = 'bg-bg-secondary text-fg-brand-primary border-border-brand';
const TOPIC_TAG_CHIP = 'bg-bg-secondary text-fg-secondary border-border-primary';

interface ITagListProps {
  tags: string[];
}

const TagList = ({ tags }: ITagListProps) => {
  if (tags.length === 0) {
    return <span className="text-fg-quaternary text-[10px]">—</span>;
  }
  const sorted = [...tags].sort((a, b) => a.localeCompare(b));
  return (
    <div className="flex w-[200px] gap-1 overflow-hidden">
      {sorted.map((tag) => (
        <span
          key={tag}
          title={tag}
          className={cx(TAG_CHIP_BASE, tag.startsWith(SYS_TAG_PREFIX) ? SYS_TAG_CHIP : TOPIC_TAG_CHIP)}
        >
          {tag}
        </span>
      ))}
    </div>
  );
};

function compareRows(a: MemoryRow, b: MemoryRow, key: SortKey, userEmailById: Map<string, string>): number {
  switch (key) {
    case 'scope':
      return a.scope.localeCompare(b.scope);
    case 'importance':
      return a.importance - b.importance;
    case 'effectiveImportance':
      return a.effectiveImportance - b.effectiveImportance;
    case 'body':
      return a.body.localeCompare(b.body);
    case 'tags':
      // Sort each set first so identical tags in different insertion order compare equal.
      return [...a.tags]
        .sort()
        .join(',')
        .localeCompare([...b.tags].sort().join(','));
    case 'source':
      return a.source.localeCompare(b.source);
    case 'sourceThread':
      return (a.sourceThreadId ?? '').localeCompare(b.sourceThreadId ?? '');
    case 'user': {
      const aEmail = userEmailById.get(a.userId) ?? a.userId;
      const bEmail = userEmailById.get(b.userId) ?? b.userId;
      return aEmail.localeCompare(bEmail);
    }
    case 'updatedAt':
      return new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
    case 'createdAt':
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    default:
      return 0;
  }
}

export default AssistantMemoryScene;
